import React, { useState } from 'react';
import { X, Save, Upload, ExternalLink, User, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/Button';
import { fileRequestApi } from '../lib/api';

interface EditorProgress {
  id: string;
  name: string;
  display_name: string;
  status: string;
  num_creatives_assigned?: number;
  actual_uploads?: number;
  creatives_completed?: number;
  deliverables_uploaded?: number;
}

interface MarkProgressModalProps {
  requestId: string;
  requestTitle: string;
  editors: EditorProgress[];
  onClose: () => void;
  onUpdate: () => void;
}

export function MarkProgressModal({ requestId, requestTitle, editors, onClose, onUpdate }: MarkProgressModalProps) {
  // Track per-editor input values: initialize with current deliverables_uploaded (or creatives_completed)
  const [editorValues, setEditorValues] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    for (const editor of editors) {
      initial[editor.id] = Number(editor.deliverables_uploaded ?? editor.creatives_completed ?? 0) || 0;
    }
    return initial;
  });

  const [notes, setNotes] = useState<Record<string, string>>({});
  // Actual AI spend incurred on the creatives produced — required by the backend
  // only on requests that carry a cost estimate.
  const [costs, setCosts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSaveEditor = async (editorId: string) => {
    const count = editorValues[editorId] ?? 0;
    const note = notes[editorId] || '';
    const rawCost = (costs[editorId] || '').trim();
    const cost = rawCost === '' ? undefined : Number(rawCost);

    if (cost !== undefined && (!Number.isFinite(cost) || cost < 0)) {
      setErrors(prev => ({ ...prev, [editorId]: 'Cost must be a non-negative number' }));
      return;
    }

    setSaving(prev => ({ ...prev, [editorId]: true }));
    setErrors(prev => ({ ...prev, [editorId]: '' }));
    setSaved(prev => ({ ...prev, [editorId]: false }));

    try {
      await fileRequestApi.markProgress(requestId, {
        completedCount: count,
        note: note || undefined,
        editorId,
        actual_cost_usd: cost,
      });
      setSaved(prev => ({ ...prev, [editorId]: true }));
      setTimeout(() => setSaved(prev => ({ ...prev, [editorId]: false })), 3000);
    } catch (err: any) {
      setErrors(prev => ({
        ...prev,
        [editorId]: err.response?.data?.error || 'Failed to update progress',
      }));
    } finally {
      setSaving(prev => ({ ...prev, [editorId]: false }));
    }
  };

  const handleSaveAll = async () => {
    for (const editor of editors) {
      const currentTotal = editor.deliverables_uploaded ?? editor.creatives_completed ?? 0;
      const newValue = editorValues[editor.id] ?? 0;
      // Only save if value changed
      if (newValue !== currentTotal) {
        await handleSaveEditor(editor.id);
      }
    }
    onUpdate();
  };

  const handleClose = () => {
    // If any were saved, trigger refresh
    if (Object.values(saved).some(Boolean)) {
      onUpdate();
    }
    onClose();
  };

  // Calculate totals
  const totalAssigned = editors.reduce((sum, e) => sum + (e.num_creatives_assigned || 0), 0);
  const totalCompleted = editors.reduce((sum, e) => sum + (editorValues[e.id] ?? 0), 0);
  const totalActualUploads = editors.reduce((sum, e) => sum + (e.actual_uploads ?? 0), 0);
  const overallPercent = totalAssigned > 0 ? Math.min(100, Math.round((totalCompleted / totalAssigned) * 100)) : 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={handleClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Mark Editor Progress</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate max-w-md">{requestTitle}</p>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Overall Progress Bar */}
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
              <span>Overall Progress</span>
              <span className="font-medium">{totalCompleted} / {totalAssigned || '?'} deliverables ({overallPercent}%)</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  overallPercent >= 100 ? 'bg-green-500' : overallPercent > 50 ? 'bg-blue-500' : overallPercent > 0 ? 'bg-amber-500' : 'bg-gray-300'
                }`}
                style={{ width: `${Math.min(overallPercent, 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Editor List */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {editors.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <User className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No editors assigned to this request</p>
            </div>
          ) : (
            editors.map((editor) => {
              const assigned = editor.num_creatives_assigned || 0;
              const actualUploads = editor.actual_uploads ?? 0;
              const currentValue = editorValues[editor.id] ?? 0;
              const editorPercent = assigned > 0 ? Math.min(100, Math.round((currentValue / assigned) * 100)) : 0;
              const externalCount = Math.max(0, currentValue - actualUploads);
              const isSaving = saving[editor.id];
              const isSaved = saved[editor.id];
              const error = errors[editor.id];

              return (
                <div
                  key={editor.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50"
                >
                  {/* Editor Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-white">
                          {editor.display_name || editor.name}
                        </p>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          editor.status === 'completed' ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' :
                          editor.status === 'in_progress' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300' :
                          editor.status === 'reassigned' ? 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300' :
                          'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}>
                          {editor.status}
                        </span>
                      </div>
                    </div>

                    {/* Save per-editor */}
                    <Button
                      onClick={() => handleSaveEditor(editor.id)}
                      disabled={isSaving}
                      size="sm"
                      className={`${
                        isSaved
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-amber-600 hover:bg-amber-700'
                      } text-white min-w-[80px]`}
                    >
                      {isSaving ? (
                        <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full inline-block" />
                      ) : isSaved ? (
                        <><CheckCircle className="w-3.5 h-3.5 mr-1" /> Saved</>
                      ) : (
                        <><Save className="w-3.5 h-3.5 mr-1" /> Save</>
                      )}
                    </Button>
                  </div>

                  {/* Stats Row */}
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div className="text-center p-2 bg-white dark:bg-gray-900 rounded-md border border-gray-200 dark:border-gray-700">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Assigned</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">{assigned || '-'}</p>
                    </div>
                    <div className="text-center p-2 bg-white dark:bg-gray-900 rounded-md border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-center gap-1">
                        <Upload className="w-3 h-3 text-blue-500" />
                        <p className="text-xs text-gray-500 dark:text-gray-400">Uploaded</p>
                      </div>
                      <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{actualUploads}</p>
                    </div>
                    <div className="text-center p-2 bg-white dark:bg-gray-900 rounded-md border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-center gap-1">
                        <ExternalLink className="w-3 h-3 text-amber-500" />
                        <p className="text-xs text-gray-500 dark:text-gray-400">External</p>
                      </div>
                      <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{externalCount}</p>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                      <span>Progress</span>
                      <span>{currentValue} / {assigned || '?'} ({editorPercent}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          editorPercent >= 100 ? 'bg-green-500' : editorPercent > 50 ? 'bg-blue-500' : editorPercent > 0 ? 'bg-amber-500' : 'bg-gray-300'
                        }`}
                        style={{ width: `${Math.min(editorPercent, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Input Row */}
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Total Completed (including external uploads)
                      </label>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditorValues(prev => ({
                            ...prev,
                            [editor.id]: Math.max(0, (prev[editor.id] ?? 0) - 1)
                          }))}
                          className="w-8 h-8 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center font-bold text-lg"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          min={0}
                          max={assigned || 9999}
                          value={editorValues[editor.id] ?? 0}
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            if (!isNaN(val) && val >= 0) {
                              setEditorValues(prev => ({ ...prev, [editor.id]: val }));
                            }
                          }}
                          className="w-20 h-8 text-center border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm font-medium focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        />
                        <button
                          onClick={() => setEditorValues(prev => ({
                            ...prev,
                            [editor.id]: (prev[editor.id] ?? 0) + 1
                          }))}
                          className="w-8 h-8 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center font-bold text-lg"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Note (optional)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., Uploaded via Dropbox"
                        value={notes[editor.id] || ''}
                        onChange={(e) => setNotes(prev => ({ ...prev, [editor.id]: e.target.value }))}
                        className="w-full h-8 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      />
                    </div>
                    <div className="w-32">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Cost incurred ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={costs[editor.id] || ''}
                        onChange={(e) => setCosts(prev => ({ ...prev, [editor.id]: e.target.value }))}
                        title="Total spend on the videos/images this editor generated"
                        className="w-full h-8 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      />
                    </div>
                  </div>

                  {/* Error message */}
                  {error && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                      <AlertCircle className="w-3.5 h-3.5" />
                      {error}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              <span className="inline-flex items-center gap-1">
                <Upload className="w-3 h-3" /> Uploaded = auto-tracked from system
              </span>
              <span className="mx-2">|</span>
              <span className="inline-flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> External = Dropbox, WeTransfer, etc.
              </span>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleClose} variant="outline" size="sm">
                Close
              </Button>
              <Button
                onClick={handleSaveAll}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Save className="w-4 h-4 mr-1" />
                Save All Changes
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
